import type { ReactNode } from "react";
import styled from "styled-components";

import { colors } from "../../../../styles/figmaColors";
import { typographies } from "../../../../styles/figmaTypographies";
import { StyledColumn, StyledFlex } from "../layout";
import { Spacer } from "../../Spacer";

export type ButtonTone = "light" | "dark";

// Figma dark pill fill — the palette has no translucent-white token.
const DARK_FILL = "rgba(255, 255, 255, 0.08)";

const TONE_STYLES: Record<
  ButtonTone,
  { background: string; text: string; border: string }
> = {
  light: {
    background: colors["colors/button/tertiary/default"],
    text: colors["colors/text/black"],
    border: colors["colors/borders/button/primary"],
  },
  dark: {
    background: DARK_FILL,
    text: colors["colors/text/white"],
    border: colors["colors/borders/button/primary"],
  },
};

export interface ButtonProps {
  children: ReactNode;
  icon?: ReactNode;
  tone?: ButtonTone;
  onClick?: () => void;
}

export const Button = ({
  children,
  icon,
  tone = "light",
  onClick,
}: ButtonProps) => (
  <StyledButton type="button" $tone={tone} onClick={onClick}>
    <Spacer x={1} />
    <StyledColumn>
      <Spacer y={0.625} />
      <StyledContent>
        {icon ? (
          <>
            <StyledIcon>{icon}</StyledIcon>
            <Spacer x={0.5} />
          </>
        ) : null}
        <StyledLabel>{children}</StyledLabel>
      </StyledContent>
      <Spacer y={0.625} />
    </StyledColumn>
    <Spacer x={1} />
  </StyledButton>
);

const StyledButton = styled.button<{ $tone: ButtonTone }>`
  /* Reset the native button chrome so spacing comes only from Spacer. */
  all: unset;
  display: inline-flex;
  align-items: center;
  border: 0.0625rem solid ${({ $tone }) => TONE_STYLES[$tone].border};
  border-radius: 25rem;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].text};
  cursor: pointer;
`;

const StyledContent = styled(StyledFlex)`
  align-items: center;
`;

const StyledIcon = styled(StyledFlex)`
  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: currentColor;
  }
`;

const StyledLabel = styled.span`
  ${typographies["Body/L"]}
`;
